import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Dialog, DialogContent, DialogTitle, DialogActions, Button, Grid } from '@mui/material';
import { Fullscreen, FullscreenExit, BarChart, TrackChanges, TrendingUp, Balance, Check } from '@mui/icons-material';
import {
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import { Prediction, ForecastResults } from '../../types';

interface CashFlowChartProps {
  prediction: Prediction;
  interestRate?: number;
  purchasePrice?: number;
  actualCashFlow?: {
    cashFlow: { [month: string]: number };
    totalCases: number;
    purchasePrice: number;
    initialDebt: number;
    creditTypeNames?: string;
  } | null;
  intervalDays: number; // number of days in each forecast period
  startDate: string; // start date for both forecast and actual (ISO string)
};

export const CashFlowChart: React.FC<CashFlowChartProps> = ({
  prediction,
  interestRate: annualInterestRate,
  purchasePrice,
  actualCashFlow,
  intervalDays,
  startDate,
}) => {
  const [fullscreen, setFullscreen] = useState(false);

  // Helper to add days to a date
  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  type ForecastPoint = {
    date: Date;
    daysFromStart: number;
    debtPurchaseReturn: number;
    investmentReturn: number;
    difference: number;
    confidenceLower?: number;
    confidenceUpper?: number;
    purchasePrice: number;
    isBreakEven?: boolean;
  };
  type ActualPoint = {
    date: Date;
    actualCashFlow: number;
  };
  type ChartData = {
    forecastPoints: ForecastPoint[];
    actualPoints: ActualPoint[];
    breakEvenPoint: ForecastPoint | null;
  };

  const chartData: ChartData = useMemo<ChartData>(() => {
    if (!prediction.results) return { forecastPoints: [], actualPoints: [], breakEvenPoint: null };
    try {
      const results: ForecastResults = JSON.parse(prediction.results);
      const forecasts = results.aggregatedForecastedReturns;
      if (!forecasts || forecasts.length === 0) return { forecastPoints: [], actualPoints: [], breakEvenPoint: null };
      let breakEvenIndex: number | null = null;
      const periodInterestRate = (annualInterestRate && intervalDays)
        ? Math.pow(1 + annualInterestRate / 100, intervalDays / 365) - 1
        : 0;
      const confidenceIntervalPresent = forecasts.some(point => typeof point.confidenceIntervalLower === 'number' && typeof point.confidenceIntervalUpper === 'number');
      const zerothElement = {
        forecastedReturn: 0,
        confidenceIntervalLower: confidenceIntervalPresent ? 0 : undefined,
        confidenceIntervalUpper: confidenceIntervalPresent ? 0 : undefined,
      };
      const forecastStart = new Date(startDate);
      const forecastPoints: ForecastPoint[] = [zerothElement, ...forecasts].map((point, index) => {
        let debtPurchaseReturn = point.forecastedReturn ?? 0;
        if (purchasePrice) {
          debtPurchaseReturn -= purchasePrice;
        }
        let investmentReturn = 0;
        if (annualInterestRate && purchasePrice) {
          const investmentValue = purchasePrice * Math.pow(1 + periodInterestRate, index + 1);
          investmentReturn = investmentValue - purchasePrice;
          if (breakEvenIndex == null) {
            if (debtPurchaseReturn >= investmentReturn) {
              breakEvenIndex = index + 1;
            }
          }
        }
        const confidenceLower = typeof point.confidenceIntervalLower === 'number'
          ? point.confidenceIntervalLower - (purchasePrice || 0)
          : undefined;
        const confidenceUpper = typeof point.confidenceIntervalUpper === 'number'
          ? point.confidenceIntervalUpper - (purchasePrice || 0)
          : undefined;
        return {
          date: addDays(forecastStart, index * intervalDays),
          daysFromStart: index * intervalDays,
          debtPurchaseReturn,
          investmentReturn,
          difference: debtPurchaseReturn - investmentReturn,
          confidenceLower,
          confidenceUpper,
          purchasePrice: purchasePrice ? -purchasePrice : 0,
          isBreakEven: breakEvenIndex === index,
        };
      });
      const maxForecastDate = addDays(forecastStart, forecasts.length * intervalDays);

      // Actual points: use startDate and +i months for each point
      let actualPoints: ActualPoint[] = [];
      if (actualCashFlow && actualCashFlow.cashFlow) {
        const actualEntries = Object.entries(actualCashFlow.cashFlow).sort();
        const actualStart = new Date(startDate);
        actualPoints = actualEntries.filter(([,], idx) => {
          // Filter out points that are beyond the forecast range (with some buffer)
          const pointDate = new Date(actualStart);
          pointDate.setMonth(pointDate.getMonth() + idx);
          return pointDate <= addDays(maxForecastDate, 30); // allow 30 days buffer after last forecast point
        })
        .map(([, value], idx) => {
          let actualValue = value;
          if (purchasePrice == null && actualEntries.length > 0) {
            actualValue = value - actualEntries[0][1];
          }
          if (prediction.totalInitialDebt && actualCashFlow.initialDebt && actualCashFlow.initialDebt > 0) {
            const normalizationFactor = prediction.totalInitialDebt / actualCashFlow.initialDebt;
            actualValue *= normalizationFactor;
          }
          // Date is startDate + idx months
          const date = new Date(actualStart);
          date.setMonth(date.getMonth() + idx);
          const daysFromStart = Math.round((date.getTime() - actualStart.getTime()) / (1000 * 60 * 60 * 24));
          return {
            date,
            daysFromStart,
            actualCashFlow: actualValue,
          };
        });
      }
      const breakEvenPoint = breakEvenIndex != null ? forecastPoints[breakEvenIndex - 1] : null;
      return { forecastPoints, actualPoints, breakEvenPoint };
    } catch (error) {
      console.error('Failed to parse prediction results:', error);
      return { forecastPoints: [], actualPoints: [], breakEvenPoint: null };
    }
  }, [prediction.results, prediction.totalInitialDebt, annualInterestRate, purchasePrice, actualCashFlow, startDate, intervalDays]);

  const { forecastPoints, actualPoints, breakEvenPoint } = chartData;

  // Calculate Y axis min/max from both forecastPoints and actualPoints, with 5% buffer
  const allYValues = [
    ...forecastPoints.map(p => [p.debtPurchaseReturn, p.investmentReturn, p.confidenceLower, p.confidenceUpper]).flat().filter(v => typeof v === 'number'),
    ...actualPoints.map(p => p.actualCashFlow)
  ];
  let yMin = Math.min(...allYValues);
  let yMax = Math.max(...allYValues);
  if (yMin === yMax) {
    yMin = yMin * 0.95;
    yMax = yMax * 1.05;
  } else {
    const buffer = (yMax - yMin) * 0.05;
    yMin -= buffer;
    yMax += buffer;
  }

  // Calculate X axis min/max for forecastPoints
  const xMin = 0;
  let xMax = forecastPoints.length > 0 ? forecastPoints[forecastPoints.length - 1].daysFromStart : 0;
  xMax = xMax + 30;

  if (!forecastPoints || forecastPoints.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nincs megjeleníthető adat
        </Typography>
      </Box>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={forecastPoints}
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          type="number"
          domain={[xMin, xMax]}
          dataKey="daysFromStart"
          label={{ value: 'Dátum', position: 'insideBottom', offset: -10 }}
          tickFormatter={(value) => {
            const date = addDays(new Date(startDate), value);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          }}
        />
        <YAxis
          domain={[yMin, yMax]}
          tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
          label={{ value: 'HUF (millió)', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip
          formatter={(value: number, name: string) => {
            if (name.includes('Különbség')) {
              const diff = value as number;
              const color = diff > 0 ? '#4caf50' : '#f44336';
              const text = diff > 0
                ? `Követelés jobb: +${formatCurrency(Math.abs(diff))}`
                : `Befektetés jobb: ${formatCurrency(diff)}`;
              return [
                <span key="diff" style={{ color, fontWeight: 'bold' }}>{text}</span>,
                name
              ];
            }
            return [formatCurrency(value), name];
          }}
          content={({ label, payload, active }) => {
            if (!active || !payload || payload.length === 0) return null;
            const date = payload[0].payload?.date;
            return (
              <div style={{ background: 'rgba(255,255,255,0.95)', padding: 12, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <div style={{ fontWeight: 600, color: '#1976d2', marginBottom: 6 }}>
                  <span style={{ color: '#1976d2' }}>{date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : label}</span>
                </div>
                {payload.map((entry, idx) => (
                  <div key={idx} style={{ color: entry.color, marginBottom: 2, fontWeight: 500 }}>
                    <span style={{ color: entry.color }}>{entry.name}:</span> {typeof entry.value === 'number' ? formatCurrency(entry.value) : entry.value}
                  </div>
                ))}
              </div>
            );
          }}
          contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
        />
        <Legend />

        {/* Zero line */}
        <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />

        {/* Confidence interval area (80% valószínűségi sáv) */}
        {forecastPoints[0].confidenceLower !== undefined && (
          <Area
            type="monotone"
            dataKey="confidenceLower"
            stroke="none"
            fill="#90caf9"
            fillOpacity={0.2}
            name="80% sáv alja"
          />
        )}
        {forecastPoints[0].confidenceUpper !== undefined && (
          <Area
            type="monotone"
            dataKey="confidenceUpper"
            stroke="none"
            fill="#90caf9"
            fillOpacity={0.2}
            name="80% sáv teteje"
          />
        )}

        {/* Purchase price line (vételár) */}
        {purchasePrice && (
          <Line
            type="monotone"
            dataKey="purchasePrice"
            stroke="#ff9800"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Vételár"
          />
        )}

        {/* Investment return line (befektetés hozama) - alternatíva */}
        {annualInterestRate && purchasePrice && (
          <Line
            type="monotone"
            dataKey="investmentReturn"
            stroke="#9c27b0"
            strokeWidth={2}
            strokeDasharray="8 4"
            dot={false}
            activeDot={{ r: 6 }}
            name={`Befektetés hozama (${annualInterestRate}% éves)`}
          />
        )}

        {/* Debt purchase return line (követelés vásárlás hozama) */}
        <Line
          type="monotone"
          dataKey="debtPurchaseReturn"
          stroke="#4caf50"
          strokeWidth={3}
          dot={(props: any) => {
            return <circle key={`debt-dot-${props.index}`} cx={props.cx} cy={props.cy} r={4} fill="#4caf50" />;
          }}
          activeDot={{ r: 6 }}
          name="Követelés vásárlás hozama"
        />

        {/* Actual cash flow line (tényadat) */}
        {actualPoints.length > 0 && (
          <Line
            key="actual-cash-flow-line"
            type="monotone"
            dataKey="actualCashFlow"
            stroke="#ff6b6b"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            isAnimationActive={true}
            animationDuration={300}
            activeDot={{ r: 6 }}
            name="Tényadat cash flow"
            data={actualPoints}
          />
        )}

        {/* Difference line (különbség) - optional, shows which is better */}
        {annualInterestRate && purchasePrice && (
          <Line
            type="monotone"
            dataKey="difference"
            stroke="#ff9800"
            strokeWidth={2}
            strokeDasharray="2 2"
            dot={false}
            name="Különbség (követelés - befektetés)"
          />
        )}

        {/* Break-even point vertical line */}
        {breakEvenPoint && (
          <ReferenceLine
            x={breakEvenPoint.daysFromStart}
            stroke="#ff0000"
            strokeWidth={2}
            strokeDasharray="6 4"
            label={{
              value: `Metszéspont (${breakEvenPoint.date.getFullYear()}-${String(breakEvenPoint.date.getMonth() + 1).padStart(2, '0')})`,
              position: "top",
              fill: "#ff0000",
              fontSize: 12,
              fontWeight: 'bold'
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderLegendInfo = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          A grafikon két alternatívát hasonlít össze:
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 1, pl: 3 }}>
          <Typography component="li" variant="caption" color="text.secondary">
            <strong style={{ color: '#4caf50' }}>Zöld vonal:</strong> Követelés vásárlás hozama
          </Typography>
          {actualCashFlow && (
            <Typography component="li" variant="caption" color="text.secondary">
              <strong style={{ color: '#ff6b6b' }}>Piros szaggatott vonal:</strong> Tényadat cash flow (normalizált, valós bevételek)
            </Typography>
          )}
          {annualInterestRate && purchasePrice && (
            <Typography component="li" variant="caption" color="text.secondary">
              <strong style={{ color: '#9c27b0' }}>Lila szaggatott vonal:</strong> Befektetés hozama ({annualInterestRate}% éves kamat, kamatos kamat)
            </Typography>
          )}
        </Box>

        {/* Data Comparison Panel - Prediction vs Actual */}
        {actualCashFlow && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, fontSize: '0.85rem' }}>
              <BarChart sx={{ fontSize: 16 }} /> Adatok Összehasonlítása
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Prediction Column */}
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <TrackChanges sx={{ fontSize: 14 }} /> Aukció Előrejelzés
                </Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    • Induló tartozás: <strong>{prediction.totalInitialDebt ? formatCurrency(prediction.totalInitialDebt) : 'N/A'}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    • Vételár: <strong>{purchasePrice ? formatCurrency(purchasePrice) : 'Nincs megadva'}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    • Ügydarabszám: <strong>-</strong> <em>(teljes portfólió)</em>
                  </Typography>
                </Box>
              </Box>

              {/* Actual Data Column */}
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <TrendingUp sx={{ fontSize: 14 }} /> Tényadatok {actualCashFlow.creditTypeNames && `(${actualCashFlow.creditTypeNames})`}
                </Typography>
                <Box sx={{ pl: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    • Induló tartozás: <strong>{formatCurrency(actualCashFlow.initialDebt)}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    • Vételár: <strong>{formatCurrency(actualCashFlow.purchasePrice)}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    • Ügydarabszám: <strong>{actualCashFlow.totalCases} db</strong>
                  </Typography>
                </Box>
              </Box>

              {/* Normalization Info */}
              {prediction.totalInitialDebt && prediction.totalInitialDebt > 0 && actualCashFlow.initialDebt > 0 && (
                <Box sx={{
                  flex: 1,
                  minWidth: 200,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.08)',
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Balance sx={{ fontSize: 14 }} /> Normalizálás
                  </Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.primary' }}>
                      Szorzó: <strong>{(prediction.totalInitialDebt / actualCashFlow.initialDebt).toFixed(2)}x</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                      A tényadat értékei normalizálva az összehasonlításhoz
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Normalization info panel - REMOVED (replaced by comparison panel above) */}

        {(() => {
          if (breakEvenPoint && annualInterestRate && purchasePrice) {
            return (
              <>
                <Box sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                  borderRadius: 1,
                  border: 1,
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Check sx={{ fontSize: 14 }} /> Metszéspont: {breakEvenPoint.date.getFullYear()}-{String(breakEvenPoint.date.getMonth() + 1).padStart(2, '0')} - Ettől válik jövedelmezőbbé a követelés vásárlás ({annualInterestRate}% befektetéshez képest)
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  A 80%-os valószínűségi sáv jelzi a modell bizonytalanságát.
                </Typography>
              </>
            );
          }
          return (
            <Typography variant="caption" color="text.secondary">
              A két vonal metszéspontja mutatja, hogy mikor válik jövedelmezőbbé a követelés vásárl a kockázatmentes befektetésnél. A 80%-os valószínűségi sáv jelzi a modell bizonytalanságát.
            </Typography>
          );
        })()}
      </Box>
    );
  };

  return (
    <>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2">
            Cash Flow (HUF)
          </Typography>
          <IconButton
            onClick={() => setFullscreen(true)}
            color="primary"
            size="small"
            title="Teljes képernyő"
          >
            <Fullscreen />
          </IconButton>
        </Box>

        {renderChart(500)}
        {renderLegendInfo()}
      </Box>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            m: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Cash Flow (HUF)
          </Typography>
          <IconButton onClick={() => setFullscreen(false)} color="primary">
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: 'calc(100% - 140px)', overflow: 'hidden', p: 0 }}>
          <Grid container sx={{ height: '100%' }}>
            {/* Chart - Left side (75%) */}
            <Grid item xs={12} md={9} sx={{ height: '100%', p: 2, overflow: 'hidden' }}>
              <Box sx={{ height: '100%' }}>
                {renderChart(window.innerHeight * 0.85 - 200)}
              </Box>
            </Grid>

            {/* Legend - Right side (25%) */}
            <Grid item xs={12} md={3} sx={{ height: '100%', p: 2, overflow: 'auto', borderLeft: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
              {renderLegendInfo()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullscreen(false)} variant="contained">
            Bezárás
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

